import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';

// ── Steps ──────────────────────────────────────────────────────────────────────
// `target`      : CSS selector — same for desktop and mobile
// `needsSidebar`: sidebar must be open for this element to be in the DOM (mobile)
// `position`    : desktop tooltip placement; mobile always bottom-sheets
const STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to Dreamy Bedtime! 🌙',
    body: "You're all set to create magical, personalised bedtime stories for your children. Let's take a quick tour — it only takes 30 seconds.",
    position: 'center',
  },
  {
    id: 'generate',
    target: '[data-tour="generate-btn"]',
    title: 'Generate a Story ✨',
    body: 'Tap here to create a brand-new story. Choose your child, pick a theme or describe your own idea — a unique story is ready in seconds.',
    position: 'bottom',
  },
  {
    id: 'children',
    target: '[data-tour="nav-children"]',
    needsSidebar: true,
    title: 'Child Profiles 👶',
    body: "Add each child's name, age, and interests. Every story is personalised to their profile — the more detail, the more magical the story.",
    position: 'right',
  },
  {
    id: 'library',
    target: '[data-tour="nav-library"]',
    needsSidebar: true,
    title: 'Story Library 📚',
    body: 'Every story you generate is saved here. Read again, listen to narration, share with family, or mark your favourites.',
    position: 'right',
  },
  {
    id: 'usage',
    target: '[data-tour="usage-card"]',
    title: 'Your Credits 💫',
    body: 'Keep an eye on your monthly story credits here. Need more? You can top up or upgrade your plan at any time.',
    position: 'top',
  },
  {
    id: 'settings',
    target: '[data-tour="nav-settings"]',
    needsSidebar: true,
    title: 'Settings & Reminders ⚙️',
    body: 'Set up bedtime reminders, customise story preferences, manage voice narration, and replay this tour from the Settings page.',
    position: 'right',
  },
];

// ── Geometry helpers ───────────────────────────────────────────────────────────

const PAD = 12; // spotlight padding around target

function getTargetRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Element must be reasonably visible
  if (r.width < 4 || r.height < 4) return null;
  return {
    top:     r.top    - PAD,
    left:    r.left   - PAD,
    width:   r.width  + PAD * 2,
    height:  r.height + PAD * 2,
    bottom:  r.bottom + PAD,
    right:   r.right  + PAD,
    centerX: r.left   + r.width  / 2,
    centerY: r.top    + r.height / 2,
  };
}

// Clamp a value into [min, max]
const clamp = (v, min, max) => Math.max(min, Math.min(v, max));

// ── ProductTour component ──────────────────────────────────────────────────────

const ProductTour = ({ onDone, onOpenSidebar, onCloseSidebar }) => {
  const [step, setStep]   = useState(0);
  const [rect, setRect]   = useState(null);
  const [vp,   setVp]     = useState({ w: window.innerWidth, h: window.innerHeight });
  const rafRef            = useRef(null);

  const isMobile = vp.w < 992; // matches Bootstrap lg breakpoint (sidebar hidden below 992px)
  const current  = STEPS[step];

  // On mobile, sidebar steps need the sidebar open first
  const needsSidebar = isMobile && current.needsSidebar;

  // Open/close sidebar when entering/leaving sidebar steps on mobile
  useEffect(() => {
    if (!isMobile) return;
    if (current.needsSidebar) {
      onOpenSidebar?.();
    } else {
      onCloseSidebar?.();
    }
  }, [step, isMobile, current.needsSidebar, onOpenSidebar, onCloseSidebar]);

  // Close sidebar when tour ends
  const handleDone = useCallback(() => {
    onCloseSidebar?.();
    onDone();
  }, [onDone, onCloseSidebar]);

  // Measure target element
  const measure = useCallback(() => {
    setRect(getTargetRect(current.target));
    setVp({ w: window.innerWidth, h: window.innerHeight });
  }, [current.target]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  // Poll until element appears — important on mobile where sidebar renders async
  useEffect(() => {
    if (!current.target) { setRect(null); return; }
    let cancelled = false;
    const poll = () => {
      if (cancelled) return;
      const r = getTargetRect(current.target);
      if (r) { setRect(r); return; }
      rafRef.current = requestAnimationFrame(poll);
    };
    rafRef.current = requestAnimationFrame(poll);
    return () => { cancelled = true; cancelAnimationFrame(rafRef.current); };
  }, [current.target, needsSidebar]); // re-poll when sidebar open state changes

  // ── Scroll spotlight into view on desktop ──
  useEffect(() => {
    if (!rect || isMobile) return;
    const el = current.target ? document.querySelector(current.target) : null;
    el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [rect, isMobile, current.target]);

  // ── Tooltip geometry ──
  const MARGIN   = 12;
  const TIP_W    = isMobile ? vp.w - MARGIN * 2 : 300;
  const TIP_H    = 190; // approximate

  let tipTop, tipLeft, arrowDir;

  if (isMobile) {
    // Mobile: pin to bottom using CSS so env(safe-area-inset-bottom) works correctly
    tipTop  = null; // unused on mobile — we use bottom positioning instead
    tipLeft = MARGIN;
    arrowDir = null;
  } else if (!rect) {
    // Desktop centred (welcome step or element not found)
    tipTop  = vp.h / 2 - TIP_H / 2;
    tipLeft = vp.w / 2 - TIP_W / 2;
    arrowDir = null;
  } else {
    const gap = 14;
    arrowDir = { bottom: 'up', top: 'down', right: 'left', left: 'right' }[current.position] || null;
    switch (current.position) {
      case 'bottom': tipTop = rect.bottom + gap;               tipLeft = rect.centerX - TIP_W / 2; break;
      case 'top':    tipTop = rect.top    - TIP_H - gap;       tipLeft = rect.centerX - TIP_W / 2; break;
      case 'right':  tipTop = rect.centerY - TIP_H / 2;        tipLeft = rect.right  + gap;         break;
      case 'left':   tipTop = rect.centerY - TIP_H / 2;        tipLeft = rect.left - TIP_W - gap;   break;
      default:       tipTop = rect.bottom + gap;               tipLeft = rect.centerX - TIP_W / 2;
    }
    tipLeft = clamp(tipLeft, MARGIN, vp.w - TIP_W - MARGIN);
    tipTop  = clamp(tipTop,  MARGIN, vp.h - TIP_H - MARGIN);
  }

  // ── Arrow style ──
  const ARROW = 8;
  const arrowStyle = arrowDir ? {
    position: 'absolute', width: 0, height: 0,
    ...(arrowDir === 'up'    && { top: -ARROW,    left: TIP_W / 2 - ARROW, borderLeft: `${ARROW}px solid transparent`, borderRight: `${ARROW}px solid transparent`, borderBottom: `${ARROW}px solid var(--card-bg)` }),
    ...(arrowDir === 'down'  && { bottom: -ARROW, left: TIP_W / 2 - ARROW, borderLeft: `${ARROW}px solid transparent`, borderRight: `${ARROW}px solid transparent`, borderTop:    `${ARROW}px solid var(--card-bg)` }),
    ...(arrowDir === 'left'  && { left: -ARROW,   top: 24,                  borderTop: `${ARROW}px solid transparent`, borderBottom: `${ARROW}px solid transparent`, borderRight: `${ARROW}px solid var(--card-bg)` }),
    ...(arrowDir === 'right' && { right: -ARROW,  top: 24,                  borderTop: `${ARROW}px solid transparent`, borderBottom: `${ARROW}px solid transparent`, borderLeft:  `${ARROW}px solid var(--card-bg)` }),
  } : null;

  // ── Spotlight ──
  // On mobile with no target: just a light overlay so content is still readable
  const overlayAlpha = (isMobile && !rect) ? 0.5 : 0.72;
  const spotlightEl  = rect
    ? (
      <div style={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        borderRadius: '10px',
        boxShadow: `0 0 0 9999px rgba(10,5,30,${overlayAlpha})`,
        zIndex: 10001,
        pointerEvents: 'none',
        // subtle glow ring around spotlight
        outline: '2px solid rgba(201,168,255,0.5)',
        outlineOffset: '2px',
      }} />
    )
    : (
      <div style={{
        position: 'fixed', inset: 0,
        background: `rgba(10,5,30,${overlayAlpha})`,
        zIndex: 10001,
        pointerEvents: 'none',
      }} />
    );

  const modal = (
    <>
      {spotlightEl}

      {/* Dismiss layer — click outside tooltip closes tour */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 10002 }} onClick={handleDone} />

      {/* Tooltip card */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          // Mobile: pin to bottom with safe-area-inset so Android nav bar doesn't overlap
          ...(isMobile
            ? { bottom: 0, left: MARGIN, right: MARGIN, width: 'auto' }
            : { top: tipTop, left: tipLeft, width: TIP_W }
          ),
          background: 'var(--card-bg)',
          borderRadius: isMobile ? '20px 20px 0 0' : '16px',
          padding: isMobile ? '20px 20px 0' : '20px',
          paddingBottom: isMobile ? 'calc(28px + env(safe-area-inset-bottom, 0px))' : undefined,
          boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
          zIndex: 10003,
        }}
      >
        {/* Mobile drag handle */}
        {isMobile && (
          <div style={{ width: '40px', height: '4px', background: 'var(--border-purple)', borderRadius: '2px', margin: '0 auto 14px' }} />
        )}

        {/* Arrow (desktop only) */}
        {arrowStyle && <div style={arrowStyle} />}

        {/* Step counter */}
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-soft)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Step {step + 1} of {STEPS.length}
        </div>

        {/* Title */}
        <div style={{ fontSize: isMobile ? '17px' : '16px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '8px', lineHeight: 1.3 }}>
          {current.title}
        </div>

        {/* Body */}
        <div style={{ fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.65, marginBottom: '16px' }}>
          {current.body}
        </div>

        {/* Dot progress */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px', gap: '4px' }}>
          {STEPS.map((_, i) => (
            <span
              key={i}
              onClick={() => setStep(i)}
              style={{
                display: 'inline-block',
                width: i === step ? '20px' : '7px',
                height: '7px',
                borderRadius: '4px',
                background: i === step ? '#6f42c1' : '#e8d5ff',
                cursor: 'pointer',
                transition: 'width 0.2s ease, background 0.2s ease',
                flexShrink: 0,
              }}
            />
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={handleDone}
            style={{ background: 'none', border: 'none', color: 'var(--text-soft)', fontSize: '12px', cursor: 'pointer', padding: '6px 0', flexShrink: 0 }}
          >
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                style={{
                  background: 'var(--header-bg)', border: 'none', borderRadius: '20px',
                  padding: isMobile ? '9px 18px' : '7px 16px',
                  fontSize: '13px', fontWeight: 600, color: 'var(--text-body)', cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={() => step < STEPS.length - 1 ? setStep(s => s + 1) : handleDone()}
              style={{
                background: 'linear-gradient(135deg,#6f42c1,#9b59b6)',
                border: 'none', borderRadius: '20px',
                padding: isMobile ? '9px 24px' : '7px 20px',
                fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer',
                boxShadow: '0 3px 12px rgba(111,66,193,0.4)',
              }}
            >
              {step === STEPS.length - 1 ? "Let's go! 🚀" : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
};

// ── useTour hook ───────────────────────────────────────────────────────────────
// Call this ONLY in DashboardPage (where all targets live).
// SettingsPage uses replayTour() which navigates to /dashboard first.

export function useTour() {
  const { user, updateUser } = useAuth();
  const navigate             = useNavigate();
  const [show, setShow]      = useState(false);

  // Auto-show on first-ever login (hasSeenTour === false from DB)
  useEffect(() => {
    if (user && user.hasSeenTour === false) {
      const t = setTimeout(() => setShow(true), 700);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Called when tour finishes or is skipped
  const doneTour = useCallback(async () => {
    setShow(false);
    try {
      await API.put('/auth/tour-seen', { seen: true });
      updateUser({ ...user, hasSeenTour: true });
    } catch (_) { /* non-critical */ }
  }, [user, updateUser]);

  // Called from Settings "Start Tour" button — navigate to Dashboard, then show
  const replayTour = useCallback(async () => {
    try {
      await API.put('/auth/tour-seen', { seen: false });
      updateUser({ ...user, hasSeenTour: false });
    } catch (_) {}
    // Navigate to dashboard — the useEffect above will fire because hasSeenTour changed
    navigate('/dashboard');
  }, [user, updateUser, navigate]);

  return { showTour: show, doneTour, replayTour };
}

export default ProductTour;
